import {Component, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../core/components/modals/base-modal/base-modal.component";
import {
  productPackageResourceType,
  productPackagesPackageType,
  productPackagesTitleModal
} from "./configs/product-packages-modal-config";
import {
  ProductPackageData,
  ProductPackageFormField,
  ProductPackagesDialogData
} from "./types/product-packages-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {productPackageFormFields} from "./configs/product-package-form-fields";
import {defaultProductPackageData} from "./configs/default-product-package";
import {ProductPackagesFilesStateService} from "./services/product-packages-files-state.service";
import {MatExpansionModule} from "@angular/material/expansion";
import {
  ProductPackagesAccordionTitleComponent
} from "./components/product-packages-accordion-title/product-packages-accordion-title.component";
import {ProductPackagesSelectDataStateService} from "./services/product-packages-select-data-state.service";
import {ProductPackagesFormComponent} from "./components/product-packages-form/product-packages-form.component";
import {LoadingService} from "../../../../../core/services/loading.service";
import {ProductPackagesFilesComponent} from "./components/product-packages-files/product-packages-files.component";
import {LoaderComponent} from "../../../../../shared/components/loader/loader.component";
import {MatButton} from "@angular/material/button";

@Component({
  selector: 'app-product-packages-modal',
  imports: [
    BaseModalComponent,
    MatExpansionModule,
    ProductPackagesAccordionTitleComponent,
    ProductPackagesFormComponent,
    ProductPackagesFilesComponent,
    LoaderComponent,
    MatButton
  ],
  templateUrl: './product-packages-modal.component.html',
  styleUrl: './product-packages-modal.component.scss',
  providers: [ LoadingService ]
})
export class ProductPackagesModalComponent extends BaseModalComponent {
  public productPackagesDialogData = inject<ProductPackagesDialogData>(MAT_DIALOG_DATA);
  private productPackagesSelectDataStateService: ProductPackagesSelectDataStateService = inject(ProductPackagesSelectDataStateService);
  private productPackagesFilesStateService: ProductPackagesFilesStateService = inject(ProductPackagesFilesStateService);

  public readonly productPackagesTitleModal = productPackagesTitleModal;
  public productPackageFormGroups = signal<Array<FormGroup>>([]);
  public productPackagesData = signal<Array<ProductPackageData>>([])
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  private packageType = signal<string>(productPackagesPackageType[this.productPackagesDialogData.modalKey]);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.productPackagesDialogData.modalKey);
  }

  public handleAddNew() {
    const formGroup = this.baseModalService.getFormGroup<ProductPackageFormField>(productPackageFormFields);
    const packageName = this.productPackagesSelectDataStateService.packageTypeSelectData().find(i => i.id.toString() === this.packageType())?.value || '';
    formGroup.get('packageType')?.setValue(this.packageType());
    formGroup.get('packageName')?.setValue(packageName);
    this.productPackageFormGroups.update(forms  => [ ...forms, formGroup]);
    this.productPackagesData.update(data  => [ ...data, defaultProductPackageData ]);
    this.isValid.set(false);
  }

  public updateProductPackageData(data: ProductPackageData, index: number): void {
    this.productPackagesData()[index] = data;
    this.productPackageFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
    this.noFirstData.set(!!this.productPackagesData().length);
  }

  public deleteProductPackage(index: number): void {
    this.removeProductPackagesAtIndex(index);
  }

  public async handleDelete(id: number, index: number): Promise<void> {
    await this.deleteRecord(productPackageResourceType.path, id)
      .then(() => this.removeProductPackagesAtIndex(index, id));
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(this.productPackagesDialogData.modalKey);
    this.loadingService.setIsLoadingState(true);
    await this.productPackagesSelectDataStateService.fetchSelectData(this.productPackagesDialogData)
      .finally(() => this.loadingService.setIsLoadingState(false));
    this.productPackagesData.set(this.productPackagesDialogData.data.productPackageDtos || []);
    this.isValid.set(!!this.productPackagesData().at(0)?.id);
    this.noFirstData.set(!!this.productPackagesData().at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.productPackagesData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.productPackagesData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<ProductPackageFormField>(productPackageFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.productPackageFormGroups.set(formGroups);
  }

  private removeProductPackagesAtIndex(index: number, id?: number): void {
    this.productPackageFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.productPackagesData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    id && this.productPackagesFilesStateService.deleteProductPackagesFilesState(id);

    if (!this.productPackagesData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }

    this.isValid.set(!!this.productPackagesData().length);
  }
}
